import { FootballMatchStatusEnum } from "../Enums/FootballMatchStatusEnum";
import MatchStatus from "../Models/Match/Status/MatchStatus";

export default class FootballMatchStatusHelper {
  private readonly ALL_FINISHED_STATUSES: FootballMatchStatusEnum[] = [
    FootballMatchStatusEnum.FINISHED,
    FootballMatchStatusEnum.CANCELLED,
    FootballMatchStatusEnum.INTERRUPTED,
    FootballMatchStatusEnum.ABANDONED,
    FootballMatchStatusEnum.FINISHED_AFTER_AWARDED_WIN,
    FootballMatchStatusEnum.FINISHED_AET,
    FootballMatchStatusEnum.FINISHED_AP,
    FootballMatchStatusEnum.FINISHED_ASG,
    FootballMatchStatusEnum.TO_FINISH,
  ];

  private readonly ALL_UPCOMING_STATUSES: FootballMatchStatusEnum[] = [
    FootballMatchStatusEnum.NOT_STARTED,
    FootballMatchStatusEnum.KICK_OFF_DELAYED,
  ];

  private readonly ALL_LIVE_STATUSES: FootballMatchStatusEnum[] = [
    FootballMatchStatusEnum.FIRST_HALF,
    FootballMatchStatusEnum.SECOND_HALF,
    FootballMatchStatusEnum.HALFTIME,
    FootballMatchStatusEnum.WAITING_FOR_PENALTY,
    FootballMatchStatusEnum.WAITING_FOR_EXTRA_TIME,
    FootballMatchStatusEnum.PENALTY,
    FootballMatchStatusEnum.PAUSE,
    FootballMatchStatusEnum.EXTRA_TIME_1ST_HALF,
    FootballMatchStatusEnum.EXTRA_TIME_2ND_HALF,
    FootballMatchStatusEnum.EXTRA_TIME_END_OF_1ST_HALF,
    FootballMatchStatusEnum.AWAITING_INFO,
  ];

  /**
   * Maps a MatchStatus to the corresponding FootballMatchStatusEnum
   * @param matchStatus The match status to map
   * @returns The corresponding FootballMatchStatusEnum
   */
  public getMatchEventStatus(
    matchStatus: MatchStatus
  ): FootballMatchStatusEnum {
    if (!matchStatus || !matchStatus.type || !matchStatus.subType) {
      return FootballMatchStatusEnum.NO_INFO_YET;
    }

    // Map based on type and subType
    const type = matchStatus.type.toLowerCase();
    const subType = matchStatus.subType.toLowerCase();

    if (type === "finished") {
      if (subType === "awarded_win")
        return FootballMatchStatusEnum.FINISHED_AFTER_AWARDED_WIN;
      if (subType === "aet") return FootballMatchStatusEnum.FINISHED_AET;
      if (subType === "ap") return FootballMatchStatusEnum.FINISHED_AP;
      if (subType === "asg") return FootballMatchStatusEnum.FINISHED_ASG;
      return FootballMatchStatusEnum.FINISHED;
    }

    if (type === "notstarted") {
      if (subType === "postponed") return FootballMatchStatusEnum.POSTPONED;
      if (subType === "delayed")
        return FootballMatchStatusEnum.KICK_OFF_DELAYED;
      return FootballMatchStatusEnum.NOT_STARTED;
    }

    if (type === "inprogress") {
      if (subType === "1st_half") return FootballMatchStatusEnum.FIRST_HALF;
      if (subType === "2nd_half") return FootballMatchStatusEnum.SECOND_HALF;
      if (subType === "halftime") return FootballMatchStatusEnum.HALFTIME;
      if (subType === "penalty") return FootballMatchStatusEnum.PENALTY;
      if (subType === "waiting_penalty")
        return FootballMatchStatusEnum.WAITING_FOR_PENALTY;
      if (subType === "pause") return FootballMatchStatusEnum.PAUSE;
      if (subType === "extra_time_1st_half")
        return FootballMatchStatusEnum.EXTRA_TIME_1ST_HALF;
      if (subType === "extra_time_2nd_half")
        return FootballMatchStatusEnum.EXTRA_TIME_2ND_HALF;
      if (subType === "extra_time_halftime")
        return FootballMatchStatusEnum.EXTRA_TIME_END_OF_1ST_HALF;
      if (subType === "waiting_extra_time")
        return FootballMatchStatusEnum.WAITING_FOR_EXTRA_TIME;
      return FootballMatchStatusEnum.AWAITING_INFO;
    }

    if (type === "interrupted") {
      if (subType === "abandoned") return FootballMatchStatusEnum.ABANDONED;
      if (subType === "to_finish") return FootballMatchStatusEnum.TO_FINISH;
      return FootballMatchStatusEnum.INTERRUPTED;
    }

    if (type === "cancelled") {
      return FootballMatchStatusEnum.CANCELLED;
    }

    return FootballMatchStatusEnum.NO_INFO_YET;
  }

  /**
   * Checks if a match is upcoming based on its status
   * @param matchStatus The match status to check
   * @returns True if the match is upcoming, false otherwise
   */
  public isUpcoming(matchStatus: MatchStatus): boolean {
    if (!matchStatus) return false;

    const eventStatus = this.getMatchEventStatus(matchStatus);
    return this.ALL_UPCOMING_STATUSES.includes(eventStatus);
  }

  /**
   * Checks if a match is live based on its status
   * @param matchStatus The match status to check
   * @returns True if the match is live, false otherwise
   */
  public isLive(matchStatus: MatchStatus): boolean {
    if (!matchStatus) return false;

    const eventStatus = this.getMatchEventStatus(matchStatus);
    return this.ALL_LIVE_STATUSES.includes(eventStatus);
  }

  /**
   * Checks if a match is finished based on its status
   * @param matchStatus The match status to check
   * @returns True if the match is finished, false otherwise
   */
  public isFinished(matchStatus: MatchStatus): boolean {
    if (!matchStatus) return false;

    const eventStatus = this.getMatchEventStatus(matchStatus);
    return this.ALL_FINISHED_STATUSES.includes(eventStatus);
  }
}
